import { fetchAuthSession } from 'aws-amplify/auth';
import { ensureAmplifyConfigured, type RuntimeConfig } from './runtime-config';

export type RepoSummary = {
  repo_id: string;
  repo_name: string;
  created_at: string;
};

export type RepoStatus = {
  state: string;
  message?: string;
  last_sync_unix_ms?: number;
  upload_count?: number;
  download_count?: number;
};

export type RepoEntry = {
  path: string;
  type: 'file' | 'dir';
  size?: number;
  mtime?: number;
  mtime_unix_ms?: number;
  is_pinned?: boolean;
  is_hydrated?: boolean;
};

export type RepoEntriesResponse = {
  path: string;
  entries: RepoEntry[];
  next_page_token?: string;
};

export type RepoDevice = {
  device_id: string;
  device_name?: string;
  platform?: string;
  last_seen_unix_ms?: number;
  last_sync_unix_ms?: number;
  state?: string;
  message?: string;
  user_upn?: string;
};

export type DeviceSummary = {
  device_id: string;
  device_name?: string;
  platform?: string;
  os_version?: string;
  client_version?: string;
  user_upn?: string;
  last_seen_unix_ms?: number;
  last_config_sync_unix_ms?: number;
  last_config_version_applied?: number;
  latest_config_version?: number;
  config_status?: 'up_to_date' | 'stale' | 'unknown';
  device_state?: 'active' | 'paused' | 'wipe_requested' | 'wiped' | 'archived';
  health_risk_level?: 'low' | 'medium' | 'high' | 'critical';
  last_heartbeat_unix_ms?: number;
  pending_backup_age_seconds?: number;
  consecutive_sync_failures?: number;
  sync_backlog_bytes?: number;
  sync_backlog_count?: number;
  snapshot_health?: 'ok' | 'degraded' | 'failed';
  snapshot_last_success_unix_ms?: number;
  last_log_upload_unix_ms?: number;
  peer_key_last_rotated_unix_ms?: number;
  paused_at_unix_ms?: number;
  wipe_requested_at_unix_ms?: number;
  wipe_applied_at_unix_ms?: number;
  archived_at_unix_ms?: number;
};

export type DeviceConfig = {
  config_version: number;
  updated_at_unix_ms?: number;
  config: Record<string, unknown>;
};

export type DeviceDetail = {
  device: DeviceSummary & { assignment_set_ids?: string[] };
  config: DeviceConfig | null;
};

export type AssignmentSet = {
  set_id: string;
  set_name: string;
  repo_ids: string[];
  updated_at_unix_ms?: number;
};

export type BootstrapConfig = Record<string, unknown>;

export type BootstrapConfigResponse = {
  config: BootstrapConfig;
  updated_at_unix_ms?: number;
  public_url?: string;
};

export class WebApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const ensureIdToken = async (): Promise<string> => {
  await ensureAmplifyConfigured();
  let session = await fetchAuthSession();
  let token = session.tokens?.idToken?.toString() ?? null;

  if (!token) {
    session = await fetchAuthSession({ forceRefresh: true });
    token = session.tokens?.idToken?.toString() ?? null;
  }

  if (!token) {
    throw new WebApiError(401, 'NotAuthenticated', 'You need to sign in before calling the API.');
  }

  return token;
};

const getWebApiBaseUrl = (config: RuntimeConfig): string => {
  const apiUrl = config.api?.webApiUrl ?? config.api?.url;
  if (!apiUrl) {
    throw new WebApiError(500, 'ApiNotConfigured', 'Web API URL is not configured.');
  }
  return apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
};

const requestJson = async <T>(
  config: RuntimeConfig,
  path: string,
  options?: {
    searchParams?: Record<string, string | undefined>;
    method?: string;
    body?: unknown;
  }
): Promise<T> => {
  const baseUrl = getWebApiBaseUrl(config);
  const url = new URL(path.replace(/^\//, ''), baseUrl);

  if (options?.searchParams) {
    for (const [key, value] of Object.entries(options.searchParams)) {
      if (value === undefined || value === '') {
        continue;
      }
      url.searchParams.set(key, value);
    }
  }

  const token = await ensureIdToken();
  const method = options?.method ?? 'GET';
  const hasBody = typeof options?.body !== 'undefined';
  const response = await fetch(url.toString(), {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(hasBody ? { 'Content-Type': 'application/json' } : {})
    },
    body: hasBody ? JSON.stringify(options?.body) : undefined
  });

  if (!response.ok) {
    let payload: { code?: string; message?: string } | undefined;
    try {
      payload = await response.json() as { code?: string; message?: string };
    } catch {
      payload = undefined;
    }

    const code = payload?.code ?? 'RequestFailed';
    const message = payload?.message ?? `Request failed (${response.status})`;
    throw new WebApiError(response.status, code, message);
  }

  return await response.json() as T;
};

export const listRepos = async (config: RuntimeConfig, tenantId: string): Promise<RepoSummary[]> => {
  const payload = await requestJson<{ repos: RepoSummary[] }>(
    config,
    `/v1/web/tenants/${tenantId}/repos`
  );
  return payload.repos ?? [];
};

export const createRepo = async (
  config: RuntimeConfig,
  tenantId: string,
  payload: { repo_name: string; repo_id?: string }
): Promise<RepoSummary> => requestJson<RepoSummary>(
  config,
  `/v1/web/tenants/${tenantId}/repos`,
  {
    method: 'POST',
    body: payload
  }
);

export const deleteRepo = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string
): Promise<{ ok: boolean }> => requestJson<{ ok: boolean }>(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}`,
  { method: 'DELETE' }
);

export const getRepoEntries = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string,
  path?: string,
  pageToken?: string,
  snapshotId?: string
): Promise<RepoEntriesResponse> => requestJson<RepoEntriesResponse>(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}/entries`,
  {
    searchParams: {
      path,
      page_token: pageToken,
      snapshot_id: snapshotId
    }
  }
);

export const getRepoStatus = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string
): Promise<RepoStatus> => requestJson<RepoStatus>(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}/status`
);

export const getRepoDevices = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string
): Promise<RepoDevice[]> => {
  const payload = await requestJson<{ devices: RepoDevice[] }>(
    config,
    `/v1/web/tenants/${tenantId}/repos/${repoId}/devices`
  );
  return payload.devices ?? [];
};

export const listDevices = async (
  config: RuntimeConfig,
  tenantId: string,
  options?: { includeArchived?: boolean }
): Promise<DeviceSummary[]> => {
  const payload = await requestJson<{ devices: DeviceSummary[] }>(
    config,
    `/v1/web/tenants/${tenantId}/devices`,
    {
      searchParams: {
        include_archived: options?.includeArchived ? 'true' : undefined
      }
    }
  );
  return payload.devices ?? [];
};

export const getDevice = async (
  config: RuntimeConfig,
  tenantId: string,
  deviceId: string
): Promise<DeviceDetail> => requestJson<DeviceDetail>(
  config,
  `/v1/web/tenants/${tenantId}/devices/${deviceId}`
);

export const updateDeviceAssignmentSets = async (
  config: RuntimeConfig,
  tenantId: string,
  deviceId: string,
  setIds: string[]
): Promise<{ ok: boolean }> => requestJson<{ ok: boolean }>(
  config,
  `/v1/web/tenants/${tenantId}/devices/${deviceId}/assignment-sets`,
  {
    method: 'PUT',
    body: { set_ids: setIds }
  }
);

export const updateDeviceState = async (
  config: RuntimeConfig,
  tenantId: string,
  deviceId: string,
  state: 'active' | 'paused' | 'wipe_requested'
): Promise<{ ok: boolean }> => requestJson<{ ok: boolean }>(
  config,
  `/v1/web/tenants/${tenantId}/devices/${deviceId}/state`,
  {
    method: 'PUT',
    body: { state }
  }
);

export const archiveDevice = async (
  config: RuntimeConfig,
  tenantId: string,
  deviceId: string
): Promise<{ ok: boolean }> => requestJson<{ ok: boolean }>(
  config,
  `/v1/web/tenants/${tenantId}/devices/${deviceId}/archive`,
  { method: 'POST' }
);

export const updateDeviceConfig = async (
  config: RuntimeConfig,
  tenantId: string,
  deviceId: string,
  payload: Record<string, unknown>
): Promise<{ config_version: number }> => requestJson<{ config_version: number }>(
  config,
  `/v1/web/tenants/${tenantId}/devices/${deviceId}/config`,
  {
    method: 'PUT',
    body: { config: payload }
  }
);

export const listAssignmentSets = async (
  config: RuntimeConfig,
  tenantId: string
): Promise<AssignmentSet[]> => {
  const payload = await requestJson<{ sets: AssignmentSet[] }>(
    config,
    `/v1/web/tenants/${tenantId}/assignment-sets`
  );
  return payload.sets ?? [];
};

export const createAssignmentSet = async (
  config: RuntimeConfig,
  tenantId: string,
  payload: { set_name: string; repo_ids: string[] }
): Promise<{ set_id: string }> => requestJson<{ set_id: string }>(
  config,
  `/v1/web/tenants/${tenantId}/assignment-sets`,
  {
    method: 'POST',
    body: payload
  }
);

export const updateAssignmentSet = async (
  config: RuntimeConfig,
  tenantId: string,
  setId: string,
  payload: { set_name: string; repo_ids: string[] }
): Promise<{ ok: boolean }> => requestJson<{ ok: boolean }>(
  config,
  `/v1/web/tenants/${tenantId}/assignment-sets/${setId}`,
  {
    method: 'PUT',
    body: payload
  }
);

export const deleteAssignmentSet = async (
  config: RuntimeConfig,
  tenantId: string,
  setId: string
): Promise<{ ok: boolean }> => requestJson<{ ok: boolean }>(
  config,
  `/v1/web/tenants/${tenantId}/assignment-sets/${setId}`,
  { method: 'DELETE' }
);

export const getBootstrapConfig = async (
  config: RuntimeConfig
): Promise<BootstrapConfigResponse> => requestJson<BootstrapConfigResponse>(
  config,
  '/v1/web/bootstrap'
);

export const updateBootstrapConfig = async (
  config: RuntimeConfig,
  payload: BootstrapConfig
): Promise<{ ok: boolean; public_url?: string }> => requestJson<{ ok: boolean; public_url?: string }>(
  config,
  '/v1/web/bootstrap',
  {
    method: 'PUT',
    body: { config: payload }
  }
);

export const getDownloadUrl = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string,
  path: string,
  snapshotId?: string
): Promise<string> => {
  const payload = await requestJson<{ url: string }>(
    config,
    `/v1/web/tenants/${tenantId}/repos/${repoId}/download`,
    {
      searchParams: { path, snapshot_id: snapshotId }
    }
  );
  if (!payload.url) {
    throw new WebApiError(500, 'InvalidResponse', 'Download URL is missing.');
  }
  return payload.url;
};

export type RepoSnapshot = {
  snapshot_id: string;
  created_at_unix_ms?: number;
  manifest_id?: string;
  index_ready?: boolean;
  restored_from_snapshot_id?: string;
};

export type RecycleBinItem = {
  path: string;
  size_bytes?: number;
  mtime_unix_ms?: number;
  last_snapshot_id: string;
  last_snapshot_created_at_unix_ms?: number;
};

export type RecycleBinResponse = {
  latest_snapshot_id: string;
  deleted_files: RecycleBinItem[];
};

export const listSnapshots = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string
): Promise<RepoSnapshot[]> => {
  const payload = await requestJson<{ snapshots: RepoSnapshot[] }>(
    config,
    `/v1/web/tenants/${tenantId}/repos/${repoId}/snapshots`
  );
  return payload.snapshots ?? [];
};

export const getRecycleBin = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string
): Promise<RecycleBinResponse> => requestJson<RecycleBinResponse>(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}/recycle-bin`
);

export const restoreFile = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string,
  path: string,
  snapshotId: string,
  destinationPath?: string
): Promise<{ snapshot_id: string; path: string }> => requestJson(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}/restore`,
  {
    method: 'POST',
    body: {
      type: 'file',
      path,
      snapshot_id: snapshotId,
      destination_path: destinationPath
    }
  }
);

export const restoreSnapshot = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string,
  snapshotId: string,
  destinationPrefix?: string,
  targetRepoId?: string
): Promise<{ snapshot_id: string; restored_from_snapshot_id: string }> => requestJson(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}/restore`,
  {
    method: 'POST',
    body: {
      type: 'snapshot',
      snapshot_id: snapshotId,
      destination_prefix: destinationPrefix,
      target_repo_id: targetRepoId
    }
  }
);

export type RepoMutationAction = 'rename' | 'move' | 'delete' | 'mkdir';
export type RepoMutationStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

export type RepoMutationResult = {
  job_id: string;
  status: RepoMutationStatus;
  snapshot_id?: string;
  manifest_id?: string;
  error?: string;
  updated_at_unix_ms?: number;
};

export const mutateRepoEntry = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string,
  action: RepoMutationAction,
  path: string,
  destinationPath?: string
): Promise<RepoMutationResult> => requestJson(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}/mutations`,
  {
    method: 'POST',
    body: {
      action,
      path,
      destination_path: destinationPath
    }
  }
);

export const getMutationStatus = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string,
  jobId: string
): Promise<RepoMutationResult> => requestJson(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}/mutations/${jobId}`
);
